import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import PageHeader from '../components/PageHeader';
import MiniFooter from '../components/shared/MiniFooter';
import AppBarSpacer from './AppBarSpacer';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';

// Import the "Couldn't connect" screenshot
const couldNotConnect = '/claude_error.png';

const PAGE_BG = '#0d0d1a';

const J = {
    text: '#b0c8e0',
    muted: '#a3d1ff',
    accent: '#66b2ff',
    surface: '#0d1f33',
    border: '0.5px solid #294766',
};

const requirements = [
    {
        number: '1',
        title: 'Node.js required',
        body: 'Connecting Claude Desktop to Jerny requires npx, which is included with Node.js. No Node.js = no connection.',
        link: { label: 'Download Node.js', href: 'https://nodejs.org' },
    },
    {
        number: '2',
        title: 'Claude Desktop required',
        body: 'Jerny is currently optimized for Claude Desktop only. Claude.ai browser is not yet supported.',
        link: { label: 'Download Claude Desktop', href: 'https://claude.ai/download' },
    },
    {
        number: '3',
        title: 'Anthropic account required',
        body: 'You need an active Claude account to use Claude Desktop.',
        link: null,
    },
    {
        number: '4',
        title: 'Phone number required',
        body: 'Anthropic requires phone verification to create an account. No phone = no account. No account = no connection.',
        link: null,
    },
];

/**
 * BeforeYouSubscribePage — prerequisites checklist before subscribing.
 * Route: /BeforeYouSubscribe
 */
export default function BeforeYouSubscribePage() {
    const navigate = useNavigate();

    return (
        <Box id="bys-root" sx={{ bgcolor: PAGE_BG, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <AppBarSpacer />

            <Box id="bys-header" sx={{ flexShrink: 0 }}>
                <Container maxWidth="md" sx={{ pt: 6, pb: 0 }}>
                    <PageHeader title="Before You Subscribe" subtitle="Make sure you're ready." />
                </Container>
            </Box>

            <Box id="bys-content" sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Container maxWidth="md" sx={{ py: 4 }}>
                    {/* Requirements list */}
                    <Box id="bys-requirements" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 6 }}>
                        {requirements.map((req) => (
                            <Box
                                key={req.number}
                                id={`bys-req-${req.number}`}
                                sx={{
                                    bgcolor: J.surface,
                                    border: J.border,
                                    borderRadius: 2,
                                    p: 3,
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'flex-start',
                                }}
                            >
                                {/* Number badge */}
                                <Box
                                    sx={{
                                        minWidth: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        border: `1px solid ${J.accent}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: J.accent,
                                        fontWeight: 700,
                                        fontSize: '0.85rem',
                                        flexShrink: 0,
                                    }}
                                >
                                    {req.number}
                                </Box>

                                <Box id={`bys-req-${req.number}-text`}>
                                    <Typography variant="subtitle1" sx={{ color: J.accent, fontWeight: 600, mb: 0.5 }}>
                                        {req.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: J.muted, lineHeight: 1.6 }}>
                                        {req.body}
                                    </Typography>
                                    {req.link && (
                                        <Typography
                                            component="a"
                                            href={req.link.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="body2"
                                            sx={{ color: J.accent, mt: 1, display: 'inline-block', textDecoration: 'underline' }}
                                        >
                                            {req.link.label} →
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    {/* Do you have all of these? */}
                    <Typography variant="h6" sx={{ color: J.text, fontWeight: 600, mb: 2, textAlign: 'center' }}>
                        Do you have all of these?
                    </Typography>
                    <Typography variant="body2" sx={{ color: J.muted, textAlign: 'center', mb: 3 }}>
                        If not, this is what's going to happen:
                    </Typography>

                    {/* Screenshot */}
                    <Box id="bys-screenshot" sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
                        <Box
                            component="img"
                            src={couldNotConnect}
                            alt="Couldn't connect to Claude error screen"
                            sx={{
                                maxWidth: 320,
                                width: '100%',
                                borderRadius: 2,
                                border: J.border,
                                boxShadow: '0 8px 32px hsla(210, 100%, 10%, 0.6)',
                            }}
                        />
                    </Box>

                    {/* Power user closing */}
                    <Box
                        id="bys-closing"
                        sx={{
                            bgcolor: J.surface,
                            border: J.border,
                            borderRadius: 2,
                            p: 4,
                            textAlign: 'center',
                            mb: 4,
                        }}
                    >
                        <Typography variant="h6" sx={{ color: J.accent, fontWeight: 600, mb: 1 }}>
                            So you are still interested?
                        </Typography>
                        <Typography variant="body1" sx={{ color: J.muted, mb: 3 }}>
                            You must be a serious power user, like us — we are here to help.
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/guide')}
                            sx={{
                                color: J.accent,
                                borderColor: J.accent,
                                '&:hover': { bgcolor: '#0d1f33', borderColor: J.accent },
                            }}
                        >
                            Follow our setup guide →
                        </Button>
                    </Box>
                </Container>
            </Box>

            <MiniFooter />
        </Box>
    );
}
